import {Trans} from '@lingui/macro';
import React, {FC, useCallback, useMemo} from 'react';
import styled from 'styled-components';

import {HandlerOf} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {Icon} from '../../../../../libs/shared/ui/src/components/icon/icon';
import {TooltipCoordinator} from '../../../../../libs/shared/ui/src/components/tooltips/tooltipCoordinator';
import {useUserTheme} from '../../../../../libs/shared/ui/src/theme/userThemeProvider';
import {useConstant} from '../../../../../srcCommon/helpers/hookHelpers';
import {ReferenceFormatModel} from '../../../../models/api/autolinkerModel';
import {StringOrTemplate} from '../../../editors/commons/stringTemplateModel';
import {getExpressionTypeIcon} from '../../../editors/expressions/expressionIcons';
import {buildStringTemplateParameter} from '../../../editors/expressions/expressionParamHelpers';
import {StringLikeResults, StringResult} from '../../../editors/expressions/infer/expressionResultModel';
import {AtomLine} from '../../../rules/ruleEditor/expressions/atomLine';
import {
  StyledParameterLineDiv,
  StyledParameterTitleLineDiv,
} from '../../../rules/ruleEditor/expressions/parameterLine';
import {RegexPlayground} from '../../../rules/ruleEditor/expressions/regexPlayground';
import {useOnSelectCreateExpressionRef} from '../../../rules/ruleEditor/hooks/useOnSelectCreateExpression';
import {InputStringTemplateParameter} from '../../../rules/ruleEditor/inputs/inputStringTemplateParameter';
import {RuleMultilineParameterCard} from '../../../rules/ruleEditor/ruleMultilineParameterCard';
import {appendExpressionRef} from '../../../rules/stringTemplateHelpers';

/*
 * Props.
 */

interface AutolinkerReferenceFormatCardProps {
  referenceFormat: ReferenceFormatModel;
  onUpdate: HandlerOf<ReferenceFormatModel>;
  isReadOnly: boolean;
}

/*
 * Styles.
 */

const PlaygroundWrapper = styled.div`
  display: flex;
  margin-top: 10px;
  justify-content: center;
`;

/*
 * Component.
 */

export const AutolinkerReferenceFormatCard: FC<AutolinkerReferenceFormatCardProps> = (props) => {
  const theme = useUserTheme();
  const {referenceFormat, onUpdate, isReadOnly} = props;

  // Matched reference can be used in expressions, but cannot use expressions itself.
  const noExpressions = useConstant(() => []);

  const patternParam = useConstant(() =>
    buildStringTemplateParameter('pattern', {
      canUseRegex: true,
    }),
  );

  const onUpdatePattern = useCallback(
    (newPattern: StringOrTemplate) => {
      onUpdate({
        ...referenceFormat,
        pattern: newPattern,
      });
    },
    [referenceFormat, onUpdate],
  );

  const onSelectCreateExpression = useOnSelectCreateExpressionRef(
    useCallback(
      (ref) => onUpdatePattern(appendExpressionRef(referenceFormat.pattern, ref)),
      [onUpdatePattern, referenceFormat.pattern],
    ),
    StringLikeResults,
  );

  const beforeParam = useConstant(() =>
    buildStringTemplateParameter('before', {
      canUseRegex: true,
    }),
  );

  const onUpdateBefore = useCallback(
    (newBefore: StringOrTemplate) => {
      onUpdate({
        ...referenceFormat,
        before: newBefore,
      });
    },
    [referenceFormat, onUpdate],
  );

  const afterParam = useConstant(() =>
    buildStringTemplateParameter('after', {
      canUseRegex: true,
    }),
  );

  const onUpdateAfter = useCallback(
    (newAfter: StringOrTemplate) => {
      onUpdate({
        ...referenceFormat,
        after: newAfter,
      });
    },
    [referenceFormat, onUpdate],
  );

  const collapsableParameters = useMemo(() => {
    const beforeNode = (
      <StyledParameterLineDiv key="before">
        <StyledParameterTitleLineDiv>
          <Trans id="rule-expressions-preceded">Only if preceded by</Trans>
        </StyledParameterTitleLineDiv>
        <InputStringTemplateParameter
          stringParameter={beforeParam}
          value={referenceFormat.before}
          onValueChange={onUpdateBefore}
          isDisabled={isReadOnly}
          expressions={noExpressions}
        />
      </StyledParameterLineDiv>
    );

    const afterNode = (
      <StyledParameterLineDiv key="after">
        <StyledParameterTitleLineDiv>
          <Trans id="rule-expressions-followed">Only if followed by</Trans>
        </StyledParameterTitleLineDiv>
        <InputStringTemplateParameter
          stringParameter={afterParam}
          value={referenceFormat.after}
          onValueChange={onUpdateAfter}
          isDisabled={isReadOnly}
          expressions={noExpressions}
        />
      </StyledParameterLineDiv>
    );

    return [beforeNode, afterNode];
  }, [
    beforeParam,
    referenceFormat.before,
    referenceFormat.after,
    onUpdateBefore,
    isReadOnly,
    noExpressions,
    afterParam,
    onUpdateAfter,
  ]);

  const title = useConstant(() => (
    <AtomLine>
      <span>
        <Trans id="autolinker-reference-format">Reference format</Trans>
      </span>
      <TooltipCoordinator
        iconColor={theme.greys.shade70}
        render={() => (
          <Trans id="autolinker-reference-format-tooltip">
            Text you enter here will be matched ignoring case. You can also use placeholders to match variable
            parts of your references.
          </Trans>
        )}
      />
    </AtomLine>
  ));

  return (
    <RuleMultilineParameterCard
      title={title}
      icon={<Icon name={getExpressionTypeIcon(StringResult).regular.filled} />}
      collapsableParameters={collapsableParameters}
      footer={
        <PlaygroundWrapper>
          <RegexPlayground
            format={referenceFormat}
            title={<Trans id="autolinker-reference-format-test">Test format</Trans>}
            description={
              <Trans id="autolinker-reference-format-help">
                Highlighted text will be extracted as references
              </Trans>
            }
          />
        </PlaygroundWrapper>
      }
    >
      <InputStringTemplateParameter
        stringParameter={patternParam}
        value={referenceFormat.pattern}
        onValueChange={onUpdatePattern}
        isDisabled={isReadOnly}
        expressions={noExpressions}
        onSelectCreateExpression={onSelectCreateExpression}
      />
    </RuleMultilineParameterCard>
  );
};
